<template>
   <teleport to="body">
    <transition name="bottom-modal">    
      <div v-if="visible" class="modal-backdrop" :style="{ zIndex: zIndex }" @click.self="close">
        <div class="modal-content" ref="modalContentRef" :style="modalContentStyle">
          <div v-if="!fullHeight" 
              class="modal-drag-handle" 
              @touchstart="handleDragStart" 
              @touchmove="handleDragMove" 
              @touchend="handleDragEnd"
              @mousedown="handleDragStart"  
              @mousemove="handleDragMove"   
              @mouseup="handleDragEnd"></div>
          
          <div class="modal-body-wrapper">
            <div class="modal-slot-content">
              <slot />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: 'BottomModal',
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
    fullHeight: {
      type: Boolean,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: 'white',
    },
    zIndex: {
      type: Number,
      default: 2000,
    },
    persistent: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['close'],
  data() {
    return {
      // Remover scrollY se não for usar 'position: fixed' para o body
      // scrollY: 0, 
      
      // Dados para a funcionalidade de arrastar (mantidos)
      isDragging: false,
      startY: 0,
      currentModalHeight: 0,
      
      modalOpenHeight: 0, 
      initialDragStartHeight: 0, 

      // Armazena os estilos originais do body para restaurar
      originalBodyPaddingRight: '',
      originalBodyOverflow: '',
      originalBodyPosition: '', // Adicionado para ser mais seguro
      originalBodyTop: '',      // Adicionado para ser mais seguro
      originalBodyWidth: '',    // Adicionado para ser mais seguro
    };
  },
  computed: {
    modalContentStyle() {
      const style = {
        'background-color': this.backgroundColor
      };

      if (this.fullHeight) {
        Object.assign(style, {
          'height': '100% !important',
          'max-height': '100% !important',
          'width': '100% !important',
          'max-width': '100% !important',
          'border-top-left-radius': '0 !important',
          'border-top-right-radius': '0 !important',
          'transition': 'none'
        });
      } else {
        Object.assign(style, {
          'max-height': `${this.currentModalHeight}px !important`,
          'transition': this.isDragging ? 'none' : 'max-height 0.2s ease-out'
        });
      }
      return style;
    }
  },
  methods: {
    close() {
      if (this.persistent) return;
      this.$emit('close');
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && !this.persistent) {
        this.close();
      }
    },
    // --- Lógica de bloqueio de scroll com JavaScript ---
    setBodyScrollLock() {
      // 1. Armazenar os estilos originais do body
      this.originalBodyPaddingRight = document.body.style.paddingRight;
      this.originalBodyOverflow = document.body.style.overflow;
      this.originalBodyPosition = document.body.style.position;
      this.originalBodyTop = document.body.style.top;
      this.originalBodyWidth = document.body.style.width;

      // 2. Calcular a largura da barra de rolagem (se existir)
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

      // 3. Aplicar estilos para bloquear o scroll
      document.body.style.overflow = 'hidden';

      // 4. Compensar a largura da barra de rolagem (se existir)
      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }
      
      // Opcional: Se ainda houver pulos em iOS, você pode tentar isso:
      // this.scrollY = window.scrollY; // Armazena a posição de scroll
      // document.body.style.position = 'fixed';
      // document.body.style.top = `-${this.scrollY}px`;
      // document.body.style.width = '100%';
    },
    restoreBodyScroll() {
      // 1. Restaurar os estilos originais do body
      document.body.style.paddingRight = this.originalBodyPaddingRight;
      document.body.style.overflow = this.originalBodyOverflow;
      document.body.style.position = this.originalBodyPosition;
      document.body.style.top = this.originalBodyTop;
      document.body.style.width = this.originalBodyWidth;

      // Opcional: Se usou a parte de 'position: fixed' para iOS
      // if (this.scrollY !== undefined) {
      //   window.scrollTo(0, this.scrollY);
      //   this.scrollY = undefined; // Reseta
      // }
    },
    
    // --- Métodos para a funcionalidade de arrastar (mantidos sem alteração na lógica) ---
    calculateModalInitialHeight() {
      if (this.fullHeight) {
        this.currentModalHeight = window.innerHeight;
        this.modalOpenHeight = window.innerHeight;
        return;
      }

      const viewportHeight = window.innerHeight;
      const topMargin = 20;
      const maxAllowedHeight = viewportHeight - topMargin;
      const minContentHeightOnOpen = 150;

      let calculatedInitialHeight = Math.min(
        (this.$refs.modalContentRef && this.$refs.modalContentRef.scrollHeight) ? this.$refs.modalContentRef.scrollHeight + 50 : minContentHeightOnOpen, 
        maxAllowedHeight
      );
      
      this.currentModalHeight = Math.max(calculatedInitialHeight, minContentHeightOnOpen);
      this.modalOpenHeight = this.currentModalHeight;       
    },

    handleDragStart(event) {
      if (this.fullHeight) return;

      this.isDragging = true;
      this.startY = event.touches ? event.touches[0].clientY : event.clientY; 
      this.initialDragStartHeight = this.$refs.modalContentRef.offsetHeight;
      
      if (event.touches) {
        document.addEventListener('touchmove', this.handleDragMove, { passive: false });
        document.addEventListener('touchend', this.handleDragEnd);
      } else {
        document.addEventListener('mousemove', this.handleDragMove);
        document.addEventListener('mouseup', this.handleDragEnd);
      }
    },

    handleDragMove(event) {
      if (!this.isDragging || this.fullHeight) return;      

      event.preventDefault(); 

      const currentY = event.touches ? event.touches[0].clientY : event.clientY;
      const deltaY = this.startY - currentY; 

      let newHeight = this.initialDragStartHeight + deltaY;

      const viewportHeight = window.innerHeight;
      const maxAllowedHeight = viewportHeight - 20;
      
      const minAllowedDragHeight = this.modalOpenHeight; 

      newHeight = Math.max(minAllowedDragHeight, Math.min(newHeight, maxAllowedHeight));
      
      this.currentModalHeight = newHeight;
    },

    handleDragEnd() {
      if (this.fullHeight) return;

      this.isDragging = false;
      document.removeEventListener('touchmove', this.handleDragMove);
      document.removeEventListener('touchend', this.handleDragEnd);
      document.removeEventListener('mousemove', this.handleDragMove);
      document.removeEventListener('mouseup', this.handleDragEnd);

      const viewportHeight = window.innerHeight;
      const maxAllowedHeight = viewportHeight - 20;
      const minOpenHeightSnap = this.modalOpenHeight; 

      const snapThreshold = 40; 

      if (this.currentModalHeight >= (maxAllowedHeight - snapThreshold)) {
          this.currentModalHeight = maxAllowedHeight;
      } else if (this.currentModalHeight <= (minOpenHeightSnap + snapThreshold)) { 
          this.currentModalHeight = minOpenHeightSnap;
      } 
    }
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.setBodyScrollLock();
        window.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('resize', this.calculateModalInitialHeight); 
        this.$nextTick(() => { 
          this.calculateModalInitialHeight(); 
        });
      } else {
        this.restoreBodyScroll();
        window.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('resize', this.calculateModalInitialHeight);
      }
    },
    fullHeight: {
      handler(newVal) {
        if (this.visible) { 
          this.$nextTick(() => {
            this.calculateModalInitialHeight();
          });
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.visible) {
      this.setBodyScrollLock();
      window.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('resize', this.calculateModalInitialHeight);
      this.$nextTick(() => { 
        this.calculateModalInitialHeight();
      });
    }
  },
  beforeUnmount() {
    this.restoreBodyScroll();
    window.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.calculateModalInitialHeight);
    document.removeEventListener('touchmove', this.handleDragMove);
    document.removeEventListener('touchend', this.handleDragEnd);
    document.removeEventListener('mousemove', this.handleDragMove);
    document.removeEventListener('mouseup', this.handleDragEnd);
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;             /* top:0; right:0; bottom:0; left:0 */
  height: 100dvh;
  z-index: 2000; /* valor padrão; sobrescrito via prop zIndex */
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.modal-content {
  /* background-color: white; REMOVIDO: Agora é controlado pela prop */
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  
  width: 100%; 
  max-width: 560px; 
  
  display: flex;
  flex-direction: column;

  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  animation: slide-up 0.3s ease-out;
  box-shadow: 0 -2px 15px rgba(0, 0, 0, 0.15);
}

.modal-body-wrapper {
  padding: 1.5rem; 
  width: 100%;
}

.modal-slot-content {
  width: 100%;
  margin: 0 auto;   
}

/* --- Estilos para a alça de arrasto --- */
.modal-drag-handle {
  width: 50px;
  height: 5px;
  background-color: #ccc;
  border-radius: 2.5px;
  margin: 10px auto;
  cursor: grab;
  touch-action: none;
  flex-shrink: 0;
  -webkit-user-select: none;
  user-select: none;
}

/* Animation */
.bottom-modal-enter-active,
.bottom-modal-leave-active {
  transition: all 0.3s ease;
}
.bottom-modal-enter-from,
.bottom-modal-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@keyframes slide-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0%);
  }
}

/* Responsividade para telas menores */
@media (max-width: 576px) {
  .modal-body-wrapper {
    padding: 1rem; 
  }
}
</style>